import { createSelector } from 'reselect';
import { PUBLIC_FEATURE_FLAGS_RECEIVED } from '../actions/constants/ActionTypes';
import * as RequestStatus from '../actions/constants/RequestStatus';
import { actionsDispatchedSelector } from './ActionsDispatchedSelectors';
import { isAppAuthSelector } from './AuthSelectors';
import { numContactsInCrmSelector } from './ContactsSelectors';
import { marketingContactsOnRenewalSelector } from './MarketingContactsSelectors';
import { fetchProductInventoryStatus } from './ProductInventorySelectors';
import { fetchProductsStatus } from './ProductsSelectors';
import { fetchPublicMetaDataStatus } from './PublicFeatureFlagSelectors';
import { fetchPurchaseMotionStatus } from './PurchaseMotionSelectors';
import { fetchRequiredOnboardingStatus } from './RequiredOnboardingSelectors';
const numContactsSucceededCheck = state => {
  const numContactsInCrm = numContactsInCrmSelector(state);
  return numContactsInCrm !== null;
};
const marketingContactsOnRenewalSucceededCheck = state => {
  const isInApp = isAppAuthSelector(state);
  if (!isInApp) {
    return true;
  }
  const marketingContactsOnRenewal = marketingContactsOnRenewalSelector(state);
  return marketingContactsOnRenewal !== null && marketingContactsOnRenewal !== undefined;
};

// This selector replicates what the above selector does, but with the addition of
// checking if the purchase options are loading. This is because we are migrating the state
// management system from redux to global context. We will do this iteratively, and can only
// remove these selector once all API calls are moved to the global context.
export const hasPricingAndPurchasingSucceededSelector = (state, {
  isPurchaseOptionsLoading
}) => {
  const isInApp = isAppAuthSelector(state);
  const requiredOnboardingStatus = fetchRequiredOnboardingStatus(state);
  const productsStatus = fetchProductsStatus(state);
  const ownershipStatus = fetchProductInventoryStatus(state);
  const ownershipComplete = ownershipStatus === RequestStatus.SUCCEEDED || ownershipStatus === RequestStatus.FAILED || !isInApp;
  return productsStatus === RequestStatus.SUCCEEDED && ownershipComplete && numContactsSucceededCheck(state) && marketingContactsOnRenewalSucceededCheck(state) && requiredOnboardingStatus === RequestStatus.SUCCEEDED && !isPurchaseOptionsLoading;
};
export const isAppReadySelector = ({
  isPurchaseOptionsLoading
}) => createSelector([state => {
  if (isPurchaseOptionsLoading) {
    return hasPricingAndPurchasingSucceededSelector(state, {
      isPurchaseOptionsLoading
    });
  }
  return hasPricingAndPurchasingSucceededSelector(state, {
    isPurchaseOptionsLoading: false
  });
}], isAppReady => isAppReady);
export const hasPublicMetaDataSucceededSelector = state => {
  const publicRequestStatus = fetchPublicMetaDataStatus(state);
  return publicRequestStatus === RequestStatus.SUCCEEDED;
};
export const hasOwnershipStatusSucceededSelector = state => {
  return fetchProductInventoryStatus(state) === RequestStatus.SUCCEEDED;
};
export const hasProductStatusFailedSelector = state => {
  const productsStatus = fetchProductsStatus(state);
  return productsStatus === RequestStatus.FAILED;
};
export const hasOwnershipStatusFailedSelector = state => {
  const ownershipStatus = fetchProductInventoryStatus(state);
  return ownershipStatus === RequestStatus.FAILED;
};
export const hasRequiredOnboardingStatusFailedSelector = state => {
  const requiredOnboardingStatus = fetchRequiredOnboardingStatus(state);
  return requiredOnboardingStatus === RequestStatus.FAILED;
};
export const hasNumContactsFailedSelector = state => {
  return !numContactsSucceededCheck(state);
};
export const hasProductsSucceededSelector = state => {
  const productsStatus = fetchProductsStatus(state);
  return productsStatus === RequestStatus.SUCCEEDED;
};
export const canCheckForMarketingContactsOnRenewalSelector = state => {
  return hasProductsSucceededSelector(state) && hasOwnershipStatusSucceededSelector(state);
};
export const hasMarketingContactsOnRenewalFailedSelector = state => {
  return canCheckForMarketingContactsOnRenewalSelector(state) && !marketingContactsOnRenewalSucceededCheck(state);
};
export const hasPurchaseMotionsSucceededSelector = state => {
  const purchaseMotionStatus = fetchPurchaseMotionStatus(state);
  return purchaseMotionStatus === RequestStatus.SUCCEEDED;
};
export const hasPurchaseMotionsFailedSelector = state => {
  const purchaseMotionStatus = fetchPurchaseMotionStatus(state);
  return purchaseMotionStatus === RequestStatus.FAILED;
};
export const hasPricingAndPurchasingFailedSelector = state => {
  return hasProductStatusFailedSelector(state) || hasOwnershipStatusFailedSelector(state) || hasNumContactsFailedSelector(state) || hasMarketingContactsOnRenewalFailedSelector(state) || hasRequiredOnboardingStatusFailedSelector(state);
};
export const hasGatesLoadedSelector = state => {
  const isAppAuth = isAppAuthSelector(state);
  const actionsDispatched = actionsDispatchedSelector(state);

  // in-app fetches user info, which has the gates, before render
  return actionsDispatched.includes(PUBLIC_FEATURE_FLAGS_RECEIVED) || isAppAuth;
};